import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { describeAnchor } from '~/components/ReviewPanel';
import { useAuth } from '~/lib/useAuth';
import {
  VIEWPORT_LABEL,
  ago,
  authorName,
  pageLabel,
  pinLabel,
  reviewHref,
  type ReviewThread,
} from '~/lib/review';
import { useReview } from '~/lib/useReview';

/**
 * Change requests — the inbox for everything left in the editor's Comment
 * mode, across every page. Clicking a row opens the editor on that page, at
 * that pin, on the screen size it was left on (/pages?slug=…&review=<id>).
 *
 * Filters live in the URL so the digest email can link straight to
 * /review?status=open. Dev notes (kind 'dev') appear for the dev role only —
 * the API never sends them to anyone else.
 */

type StatusFilter = 'open' | 'resolved' | 'all';
type KindFilter = 'all' | 'change' | 'dev';

export function ReviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rv = useReview();
  const [actionError, setActionError] = useState<string | null>(null);

  const status = (['open', 'resolved', 'all'].includes(params.get('status') ?? '')
    ? params.get('status')
    : 'open') as StatusFilter;
  const kind = (['change', 'dev'].includes(params.get('kind') ?? '')
    ? params.get('kind')
    : 'all') as KindFilter;
  const page = params.get('page') ?? 'all';
  const isDev = user?.role === 'dev';

  function setParam(key: string, value: string, fallback: string) {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  }

  const pages = useMemo(() => [...new Set(rv.threads.map((t) => t.page))].sort(), [rv.threads]);
  const counts = useMemo(
    () => ({
      open: rv.threads.filter((t) => t.status === 'open').length,
      resolved: rv.threads.filter((t) => t.status === 'resolved').length,
    }),
    [rv.threads],
  );

  const visible = rv.threads.filter(
    (t) =>
      (status === 'all' || t.status === status) &&
      (kind === 'all' || t.kind === kind) &&
      (page === 'all' || t.page === page),
  );
  const groups = pages
    .map((p) => ({
      page: p,
      threads: visible
        .filter((t) => t.page === p)
        .sort(
          (a, b) =>
            (a.kind === b.kind ? 0 : a.kind === 'change' ? -1 : 1) || a.number - b.number,
        ),
    }))
    .filter((g) => g.threads.length > 0);

  const canResolve = (t: ReviewThread) =>
    user?.role === 'dev' || user?.role === 'admin' || t.created_by === user?.id;

  return (
    <div className="rvi">
      <h1 className="rvi__title">Change requests</h1>
      <p className="rvi__sub">
        Everything left in the editor’s Comment mode. Open one to jump to that spot on the page, on
        the same screen size.
        {isDev && ' Dev notes are only visible to you and the dev team.'}
      </p>

      <div className="rvi__bar">
        <div className="rvi__chips" role="group" aria-label="Status">
          {(
            [
              ['open', `Open (${counts.open})`],
              ['resolved', `Resolved (${counts.resolved})`],
              ['all', 'All'],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              aria-pressed={status === v}
              onClick={() => setParam('status', v, 'open')}
            >
              {label}
            </button>
          ))}
        </div>
        {isDev && (
          <div className="rvi__chips" role="group" aria-label="Type">
            {(
              [
                ['all', 'Everything'],
                ['change', 'Change requests'],
                ['dev', 'Dev notes'],
              ] as const
            ).map(([v, label]) => (
              <button
                key={v}
                type="button"
                aria-pressed={kind === v}
                onClick={() => setParam('kind', v, 'all')}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {pages.length > 1 && (
          <select
            className="rvi__select"
            aria-label="Page"
            value={page}
            onChange={(e) => setParam('page', e.target.value, 'all')}
          >
            <option value="all">All pages</option>
            {pages.map((p) => (
              <option key={p} value={p}>
                {pageLabel(p)}
              </option>
            ))}
          </select>
        )}
      </div>

      {(rv.error || actionError) && <p className="rv-error">{actionError ?? rv.error}</p>}

      {rv.loading && rv.threads.length === 0 ? (
        <p className="rvi__empty">Loading…</p>
      ) : groups.length === 0 ? (
        <p className="rvi__empty">
          {rv.threads.length === 0
            ? 'No comments yet. Open the editor, switch to Comment, and click anywhere on a page.'
            : 'Nothing matches these filters.'}
        </p>
      ) : (
        groups.map((g) => (
          <section key={g.page}>
            <h2 className="rvi__group">{pageLabel(g.page)}</h2>
            <ul className="rvi__list">
              {g.threads.map((t) => {
                const first = t.comments[0];
                return (
                  <li key={t.id} className={`rvi__row${t.status === 'resolved' ? ' is-resolved' : ''}`}>
                    <span
                      className={`rv-pin${t.kind === 'dev' ? ' rv-pin--dev' : ''}${t.status === 'resolved' ? ' rv-pin--done' : ''}`}
                      aria-hidden
                    >
                      {t.status === 'resolved' ? '✓' : pinLabel(t)}
                    </span>
                    <button
                      type="button"
                      className="rvi__main"
                      onClick={() => navigate(reviewHref(t))}
                      aria-label={`Open ${pageLabel(t.page)} comment ${pinLabel(t)}`}
                    >
                      <span className="rvi__text">{first?.body}</span>
                      <span className="rvi__meta">
                        <span className="rv-chip">{VIEWPORT_LABEL[t.viewport]}</span>
                        {t.kind === 'dev' && <span className="rv-chip">Dev note</span>}
                        <span>{authorName(t.created_by_email, user?.email)}</span>
                        <span>{ago(t.created_at)}</span>
                        {t.comments.length > 1 && <span>{t.comments.length} comments</span>}
                        {t.anchor && <span>{describeAnchor(t.anchor)}</span>}
                      </span>
                    </button>
                    <span className="rvi__side">
                      <button type="button" className="rv-btn" onClick={() => navigate(reviewHref(t))}>
                        Open →
                      </button>
                      {canResolve(t) && (
                        <button
                          type="button"
                          className="rv-btn"
                          onClick={() => {
                            setActionError(null);
                            rv.setStatus(t.id, t.status === 'open' ? 'resolved' : 'open').catch((e) =>
                              setActionError((e as Error).message),
                            );
                          }}
                        >
                          {t.status === 'open' ? 'Resolve ✓' : 'Reopen'}
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}
